<template>
  <pui-flex class="pui-file-notification" direction="column" flex="1">
    <!-- CREATING UPLOAD REQUEST -->
    <template v-if="data.state === UploadStates.CREATING">
      <pui-flex class="file-notification-title">
        <div class="notif-title-main">
          <span class="notif-title-main__picto mdi mdi-clock-outline"></span>
          {{ $t('common.upload.notification.title.creating') }}
        </div>
      </pui-flex>
      <div class="file-notification-content">
        <pui-common-progress-linear hideMessage />
      </div>
    </template>

    <!-- UPLOADING FILES OR PROCESSING THE FILES -->
    <template
      v-else-if="
        data.state === UploadStates.UPLOADING_FILES || data.state === UploadStates.PROCESSING
      "
    >
      <pui-flex class="file-notification-title">
        <span class="notif-title-main__picto mdi mdi-clock-outline"></span>
        <div class="notif-title-main">
          {{
            $t('dam.files.upload_notification.title.media_count', {
              successCount: data.successFileCount,
              totalCount: data.totalFileCount,
            })
          }}
        </div>
        <div class="notif-title-secondary">
          <template v-if="data.state === UploadStates.UPLOADING_FILES">
            {{ $t('common.upload.notification.title.uploading') }}
          </template>
          <template v-else-if="data.state === UploadStates.PROCESSING">
            {{ $t('common.upload.notification.title.files_sent') }}
          </template>
        </div>
      </pui-flex>
      <div class="file-notification-content">
        <template v-if="data.state === UploadStates.PROCESSING">
          {{ $t('common.upload.notification.message.processing') }}
        </template>
        <pui-common-progress-linear
          :determinate="data.state === UploadStates.UPLOADING_FILES"
          :value="progressPercentage"
          hideMessage
        />
        <template v-if="data.state === UploadStates.UPLOADING_FILES">
          {{ $t('common.upload.notification.message.warn_no_close') }}
        </template>
      </div>
    </template>

    <!-- UPLOAD ERROR -->
    <template v-else-if="data.state === UploadStates.DONE_ERROR">
      <pui-flex class="file-notification-title notification-error">
        <div class="notif-title-main">
          <span class="notif-title-main__picto mdi mdi-alert-circle-outline"></span>
          {{ $t('common.upload.notification.title.error') }}
        </div>
        <div class="notif-title-secondary">
          <pui-button
            @click.stop="dismissNotification"
            picto="mdi-close"
            flat
            variant="secondary"
          />
        </div>
      </pui-flex>
      <div class="file-notification-content">
        <div>
          {{ $t('common.upload.notification.message.error.primary') }}
        </div>
        <div>{{ $t('common.upload.notification.message.error.secondary') }}</div>
      </div>
    </template>

    <!-- UPLOAD & PROCESSING SUCCESS -->
    <template v-else-if="data.state === UploadStates.DONE_SUCCESS">
      <pui-flex class="file-notification-title notification-success">
        <div class="notif-title-main">
          <span class="notif-title-main__picto mdi mdi-check-circle-outline"></span>
          {{ $t('common.upload.notification.title.success') }}
        </div>
        <div class="notif-title-secondary">
          <pui-button
            @click.stop="dismissNotification"
            picto="mdi-close"
            flat
            variant="secondary"
          />
        </div>
      </pui-flex>
      <div class="file-notification-content">
        {{
          $t('dam.files.upload_notification.message.media_count.message', {
            successCount: data.successFileCount,
            totalCount: data.totalFileCount,
          })
        }}
        <span v-if="hasErroredFileUploads">
          {{ $t('common.upload.notification.message.file_count.error.of_which') }}
          <pui-button @click="openErroredFilesDialog" flat variant="tertiary">{{
            $t('common.upload.notification.message.file_count.error.corrupted_count', {
              count: data.errorFileNames.length,
            })
          }}</pui-button>
        </span>
        <pui-flex class="wrap-wrap">
          <pui-button @click="navigateToTargetFolder" flat variant="secondary">
            {{
              $t('dam.files.upload_notification.message.success.navigate_to_folder', {
                folderName: piivoTranslate(data.extras.targetFolder),
              })
            }}
          </pui-button>
        </pui-flex>
      </div>
    </template>
  </pui-flex>
</template>

<script>
import Vue from 'vue';

import puiFileUploadError from '../../../common/components/upload/FileUploadError.vue';
import services from '../../../../core/services';
import notificationItemMixin from '../../../../platform/components/notifications/notificationItemMixin';
import { FileUploadEvents } from '../../constants/file';
import { UploadStates } from '../../../common/constants/upload';

export default {
  name: 'dam-file-upload-notification',
  mixins: [notificationItemMixin],
  data() {
    return {
      UploadStates,
    };
  },
  computed: {
    /**
     * @returns {Number} the percentage of uploaded files
     */
    progressPercentage() {
      if (
        !this.data ||
        this.data.state !== UploadStates.UPLOADING_FILES ||
        this.data.totalFileCount <= 0
      ) {
        return 0;
      }

      const percentage = (this.data.successFileCount / this.data.totalFileCount) * 100;
      return Math.round(percentage);
    },
    /**
     * @returns {boolean} if there are any files that were not uploaded
     */
    hasErroredFileUploads() {
      return this.data.errorFileNames && this.data.errorFileNames.length > 0;
    },
  },
  methods: {
    // FILTERS
    piivoTranslate(value) {
      return Vue.filter('piivoTranslate')(value);
    },
    // NOTIF ACTIONS
    /**
     * Removes this notification
     *
     * @returns {void}
     */
    dismissNotification() {
      services.getService('notifications').removeNotification(this.id);
    },
    /**
     * Selects the files uploaded
     *
     * @returns {void}
     */
    navigateToFiles() {
      // TODO: https://evolutioncom.visualstudio.com/EVOLUTION.Piivo2/_workitems/edit/7181
    },
    /**
     * Opens the folder where the files were uploaded to
     *
     * @returns {void}
     */
    navigateToTargetFolder() {
      services
        .getService('uploadManager')
        .$emit(
          FileUploadEvents.NAVIGATE_TO_FOLDER,
          this.data.extras.targetFolder,
          this.data.extras
        );
    },
    // FILES
    /**
     * Opens the errored files dialog
     *
     * @returns {void}
     */
    openErroredFilesDialog() {
      this.$emit('openDialog', puiFileUploadError, {
        errorFileNames: this.data.errorFileNames,
      });
    },
  },
};
</script>
